import * as React from 'react';
import { Drawer } from 'vaul';
import { Slot } from '@radix-ui/react-slot';
import { X } from '@phosphor-icons/react';
import { cn } from '@relay-towing/tokens';
import {
  bottomSheetBackdropRecipe,
  bottomSheetBodyRecipe,
  bottomSheetFooterRecipe,
  bottomSheetHandleRecipe,
  bottomSheetHeaderRecipe,
  bottomSheetRecipe,
  type BottomSheetRecipeProps,
} from '../recipes/sheet.recipe';
import { Text } from './Text';
import { Button } from './Button';

/*
 * Whether this sheet is open inside another. A sheet opened from inside a
 * sheet has to be vaul's NestedRoot, which is what pushes the one underneath
 * back as the new one rises — "panel stacking" in the Figma. Worked out here
 * rather than asked of the caller, because a caller who forgets gets two
 * sheets drawn flat on top of each other with nothing saying which is live.
 */
const NestedContext = React.createContext(false);

/*
 * The enclosing sheet: whether it can be put away, and how to close it.
 *
 * `close` exists because vaul, told `dismissible={false}`, refuses every
 * close — its own Close button included — so the onboarding sheet's
 * Continue could not shut it. The open state lives here instead, and
 * BottomSheetClose closes through it, which works either way.
 */
interface SheetContextValue {
  dismissible: boolean;
  close: () => void;
  modal: boolean;
  snapping: boolean;
}
const SheetContext = React.createContext<SheetContextValue>({
  dismissible: true,
  close: () => {},
  modal: true,
  snapping: false,
});

export interface BottomSheetProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * `false` for a sheet the user has to answer — onboarding, a setup step.
   * Dragging, tapping the backdrop and Escape all stop closing it, and the
   * close button goes, so the sheet's own buttons are the only way out.
   */
  dismissible?: boolean;
  /**
   * Heights the sheet rests at, smallest first: a px or rem string, or a
   * fraction of the screen — `['9rem', 0.5, 1]`. Dragging settles on the
   * nearest. The call detail's sheet over the map is the case: a peek of the
   * call, half the screen, then all of it.
   */
  snapPoints?: (number | string)[];
  /** The snap point it rests at now. Leave out to let the sheet keep it. */
  activeSnapPoint?: number | string | null;
  onActiveSnapPointChange?: (snapPoint: number | string | null) => void;
  /**
   * `false` leaves the page underneath usable: no backdrop, no focus trap,
   * and taps reach the map. For a sheet that is part of the screen rather
   * than over it, which usually also wants `dismissible={false}`.
   */
  modal?: boolean;
  children?: React.ReactNode;
}

/**
 * The phone's panel: rises from the bottom, drags down to close.
 *
 * Use it for anything a phone screen opens over itself — an item's details, a
 * create form, a picker. Open a BottomSheet from inside another and it
 * stacks: the first steps back and the new one rises over it.
 *
 *   <BottomSheet>
 *     <BottomSheetTrigger asChild><Button>New call</Button></BottomSheetTrigger>
 *     <BottomSheetContent title="Create a call" size="full" footer={…}>
 *       …
 *     </BottomSheetContent>
 *   </BottomSheet>
 *
 * With `snapPoints` it rests at set heights and is dragged between them;
 * with `modal={false}` as well, it is a panel of the screen — the sheet over
 * the call detail's map — rather than something opened over it.
 *
 * On a desktop reach for Sheet or Dialog instead; Dialog already becomes a
 * bottom sheet on a phone, without the drag.
 */
export function BottomSheet({
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  dismissible = true,
  snapPoints,
  activeSnapPoint,
  onActiveSnapPointChange,
  modal = true,
  children,
}: BottomSheetProps) {
  const nested = React.useContext(NestedContext);
  const Root = nested ? Drawer.NestedRoot : Drawer.Root;

  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : uncontrolledOpen;

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!controlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [controlled, onOpenChange]
  );

  const snapping = Boolean(snapPoints?.length);
  const context = React.useMemo(
    () => ({ dismissible, close: () => setOpen(false), modal, snapping }),
    [dismissible, setOpen, modal, snapping]
  );

  /*
   * vaul types snap points as a union keyed on whether `fadeFromIndex` is
   * set, so the props go in as one object rather than three optional ones.
   */
  const snapProps = snapping
    ? {
        snapPoints,
        activeSnapPoint,
        setActiveSnapPoint: onActiveSnapPointChange,
      }
    : {};

  /*
   * The panel portals into the body of the document the sheet is in, not
   * always the page's own. Radix's default is the global document, so a sheet
   * rendered into an iframe — the library's phone frame is one — rose over
   * the page outside the phone instead of inside it. The sheet itself renders
   * no element to ask, hence the hidden marker.
   */
  const [container, setContainer] = React.useState<HTMLElement>();
  const markOwner = React.useCallback((node: HTMLSpanElement | null) => {
    const doc = node?.ownerDocument;
    if (doc && doc !== document) setContainer(doc.body);
  }, []);

  return (
    <SheetContext.Provider value={context}>
      <Root
        open={open}
        onOpenChange={setOpen}
        dismissible={dismissible}
        modal={modal}
        // vaul pins the page's body in place on Safari while a sheet is
        // open. A non-modal sheet is part of the page, open for as long as
        // the screen is, and would pin it for good.
        noBodyStyles={!modal}
        container={container}
        {...snapProps}
      >
        <span ref={markOwner} hidden />
        {children}
      </Root>
    </SheetContext.Provider>
  );
}

export const BottomSheetTrigger = Drawer.Trigger;

/**
 * Closes the sheet it is in — including one that is not dismissible, which
 * is how the buttons on an onboarding sheet let the user out.
 */
export interface BottomSheetCloseProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Close through the child — a library Button — instead of a bare button. */
  asChild?: boolean;
}

export const BottomSheetClose = React.forwardRef<
  HTMLButtonElement,
  BottomSheetCloseProps
>(function BottomSheetClose({ asChild, onClick, ...props }, ref) {
  const { close } = React.useContext(SheetContext);
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      ref={ref}
      {...(asChild ? {} : { type: 'button' as const })}
      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(event);
        if (!event.defaultPrevented) close();
      }}
      {...props}
    />
  );
});

export interface BottomSheetContentProps
  extends Omit<React.ComponentPropsWithoutRef<typeof Drawer.Content>, 'title'> {
  /**
   * `auto` is as tall as its content, `full` the whole screen for a form.
   * Ignored on a sheet with snap points, which is always drawn full height.
   */
  size?: Exclude<BottomSheetRecipeProps['size'], 'snap'>;
  /** Heading, and the sheet's accessible name. */
  title: React.ReactNode;
  /** The kind of thing the sheet is about, above its title: "Impound". */
  eyebrow?: React.ReactNode;
  description?: React.ReactNode;
  /**
   * The confirm for a creation surface — usually an icon button with a tick.
   * Supplying it switches the header to the iOS arrangement: close on the
   * left, title centred, this on the right.
   */
  action?: React.ReactNode;
  /** Buttons along the bottom edge, laid out at equal width. */
  footer?: React.ReactNode;
  /**
   * A rule above the footer even on a sheet that does not scroll, for a
   * list whose length is not known — the Operators sheet draws one, since a
   * fleet's names can run under the button. A full or snapping sheet always
   * has it.
   */
  ruledFooter?: boolean;
  /** Defaults to hidden on a sheet that is not dismissible. */
  hideCloseButton?: boolean;
  /**
   * The title is what the sheet is about rather than what it is for: a
   * call's street, at heading size in ink, its town under it as the
   * description. Most sheets keep the muted title.
   */
  prominent?: boolean;
  /** One round button at the header's right, before the close: directions. */
  aside?: React.ReactNode;
  /**
   * No header at all: the title is said to a screen reader but not drawn,
   * and there is no close button, so the footer must offer the way out.
   * The Figma's create sheet opens straight onto its first section
   * ("Details") with Cancel and Create along the bottom.
   */
  hideTitle?: boolean;
}

export const BottomSheetContent = React.forwardRef<
  React.ElementRef<typeof Drawer.Content>,
  BottomSheetContentProps
>(function BottomSheetContent(
  {
    className,
    size,
    title,
    eyebrow,
    description,
    action,
    footer,
    ruledFooter,
    hideCloseButton,
    prominent = false,
    aside,
    hideTitle = false,
    children,
    ...props
  },
  ref
) {
  const { dismissible, modal, snapping } = React.useContext(SheetContext);
  const showClose = !(hideCloseButton ?? !dismissible);

  const centered = action !== undefined;

  const close = showClose ? (
    <BottomSheetClose asChild>
      {centered ? (
        <Button variant="secondary" size="icon-lg" pill aria-label="Close">
          <X />
        </Button>
      ) : (
        <Button variant="subtle" size="icon-lg-glyph" pill aria-label="Close">
          <X />
        </Button>
      )}
    </BottomSheetClose>
  ) : null;

  return (
    <Drawer.Portal>
      {/* A non-modal sheet leaves the page usable, so nothing dims it. */}
      {modal ? (
        <Drawer.Overlay className={bottomSheetBackdropRecipe()} />
      ) : null}
      <Drawer.Content
        ref={ref}
        {...(description ? {} : { 'aria-describedby': undefined })}
        className={cn(
          bottomSheetRecipe({ size: snapping ? 'snap' : size }),
          className
        )}
        {...props}
      >
        {/* The grip says the sheet moves between heights; a sheet that only
            opens and closes has the backdrop and close button to say so. */}
        {snapping ? (
          <Drawer.Handle className={bottomSheetHandleRecipe()} />
        ) : null}
        <NestedContext.Provider value>
          {hideTitle ? (
            <Drawer.Title className="sr-only">{title}</Drawer.Title>
          ) : (
            <div
              className={bottomSheetHeaderRecipe({
                layout: centered ? 'centered' : 'standard',
              })}
            >
              {centered ? (
                <>
                  {/* Holds the column when there is no close button, so the
                    title stays centred on the panel. */}
                  {close ?? <span aria-hidden="true" />}
                  <Drawer.Title asChild>
                    <Text variant="body-strong" truncate>
                      {title}
                    </Text>
                  </Drawer.Title>
                  {action}
                </>
              ) : (
                <>
                  <div className="relay-ui-internal min-w-0 space-y-1">
                    {eyebrow ? (
                      <Text variant="body" tone="muted">
                        {eyebrow}
                      </Text>
                    ) : null}
                    {/* Muted, as the Figma's Sheet Header draws it: the
                      sheet's content is what to read, the title only
                      says what it is for. */}
                    <Drawer.Title asChild>
                      {prominent ? (
                        <Text variant="h1">{title}</Text>
                      ) : (
                        <Text variant="h2" tone="muted">
                          {title}
                        </Text>
                      )}
                    </Drawer.Title>
                    {description ? (
                      <Drawer.Description asChild>
                        <Text
                          variant={prominent ? 'body-lg' : 'body'}
                          tone="muted"
                        >
                          {description}
                        </Text>
                      </Drawer.Description>
                    ) : null}
                  </div>
                  {aside}
                  {close}
                </>
              )}
            </div>
          )}

          {centered && description ? (
            <Drawer.Description className="sr-only">
              {description}
            </Drawer.Description>
          ) : null}

          <div className={bottomSheetBodyRecipe({ headless: hideTitle })}>
            {children}
          </div>

          {footer ? (
            <div
              className={bottomSheetFooterRecipe({
                ruled: ruledFooter || snapping || size === 'full',
              })}
            >
              {footer}
            </div>
          ) : null}
        </NestedContext.Provider>
      </Drawer.Content>
    </Drawer.Portal>
  );
});
